import React, { useState, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import Step1, { Step1SimData } from './steps/step1'; // SIM
import Step2, { Step2SimData } from './steps/step2'; // Feiras da Agricultura Familiar
import Step3, { Step3SimData } from './steps/step3'; // Feiras Permanentes
import Step4, { Step4SimData } from './steps/step4'; // Feiras Não Permanentes
import Step5, { Step5SimData } from './steps/step5'; // Certificação/Identificação
import ContainerPainel from 'components/container_painel';
import { getFormularioSimByUUID, updateFormularioSimByUUID } from 'api/forms/pacote_formularios.api';
import { FormSimHorizontalStepper } from 'components/forms/form_sim_horizontal_stepper';

// Step 1: SIM (Serviço de Inspeção Municipal)
const emptyStep1: Step1SimData = {
  referenceYear: new Date().getFullYear(),
  hasSim: null,
  simActive: null,
  simEstablishments: 0,
  hasVet: null,
  joinedSusaf: null,
  wantsSusaf: null,
};

// Step 2: Feiras da Agricultura Familiar
const emptyStep2: Step2SimData = {
  referenceYear: new Date().getFullYear(),
  hasFamilyFarmersMarket: null,
  numberOfFamilyFarmers: 0,
  annualMarketValue: 0,
  totalFamilyFarmingValue: 0,
  numberOfPermanentMarkets: 0,
  numberOfNonPermanentMarkets: 0,
};

// Step 3: Feiras Permanentes
const emptyStep3: Step3SimData = {
  referenceYear: new Date().getFullYear(),
  permanentMarkets: [],
};

// Step 4: Feiras Não Permanentes
const emptyStep4: Step4SimData = {
  referenceYear: new Date().getFullYear(),
  nonPermanentMarkets: [],
};

// Step 5: Certificação/Identificação
const emptyStep5: Step5SimData = {
  hasCertification: null,
  certificationSoldInFairs: null,
  hasMunicipalSeal: null,
  municipalSealName: '',
  wantsMunicipalSeal: null,
  knowsHowToCreateSeal: null,
  referenceYear: new Date().getFullYear(),
};

export const FormularioSIMEditar: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [step1Data, setStep1Data] = useState<Step1SimData>(emptyStep1);
  const [step2Data, setStep2Data] = useState<Step2SimData>(emptyStep2);
  const [step3Data, setStep3Data] = useState<Step3SimData>(emptyStep3);
  const [step4Data, setStep4Data] = useState<Step4SimData>(emptyStep4);
  const [step5Data, setStep5Data] = useState<Step5SimData>(emptyStep5);

  useEffect(() => {
    if (uuid) {
      fetchSimFormData(uuid).then((formData) => {
        setStep1Data(formData.step1);
        setStep2Data(formData.step2);
        setStep3Data(formData.step3);
        setStep4Data(formData.step4);
        setStep5Data(formData.step5);

        console.log("TESTE", formData);
      });
    }
  }, [uuid]);

  const handleBack = () => {
    // Se NÃO possui feira, volta do Step5 para Step2
    if (activeStep === 4 && step2Data.hasFamilyFarmersMarket === false) {
      setActiveStep(1);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Step 1: SIM
    const isStep1Filled = step1Data.hasSim !== null;
    // Step 2: Feiras da Agricultura Familiar
    const isStep2Filled = step2Data.hasFamilyFarmersMarket !== null;
    // Step 3: Feiras Permanentes
    const isStep3Filled = step3Data.permanentMarkets && step3Data.permanentMarkets.length > 0;
    // Step 4: Feiras Não Permanentes
    const isStep4Filled = step4Data.nonPermanentMarkets && step4Data.nonPermanentMarkets.length > 0;
    // Step 5: Certificação/Identificação
    const isStep5Filled = step5Data.hasCertification !== null;

    let package_status: number | undefined = undefined;
    if (isStep1Filled && isStep2Filled && isStep3Filled && isStep4Filled && isStep5Filled) {
      package_status = 3;
    } else {
      package_status = 2;
    }

    const payload = {
      has_certification: step5Data.hasCertification,
      certification_sold_in_fairs: step5Data.certificationSoldInFairs,
      has_municipal_seal: step5Data.hasMunicipalSeal,
      municipal_seal_name: step5Data.municipalSealName,
      wants_municipal_seal: step5Data.wantsMunicipalSeal,
      knows_how_to_create_seal: step5Data.knowsHowToCreateSeal,
      reference_year: step1Data.referenceYear,
      has_sim: step1Data.hasSim,
      sim_active: step1Data.simActive,
      sim_establishments: step1Data.simEstablishments,
      has_vet: step1Data.hasVet,
      joined_susaf: step1Data.joinedSusaf,
      wants_susaf: step1Data.wantsSusaf,
      has_family_farmers_market: step2Data.hasFamilyFarmersMarket,
      number_of_family_farmers: step2Data.numberOfFamilyFarmers,
      annual_market_value: step2Data.annualMarketValue,
      total_family_farming_value: step2Data.totalFamilyFarmingValue,
      number_of_permanent_markets: step2Data.numberOfPermanentMarkets,
      number_of_non_permanent_markets: step2Data.numberOfNonPermanentMarkets,
      permanent_markets: step3Data.permanentMarkets,
      non_permanent_markets: step4Data.nonPermanentMarkets,
      package_status,
    };

    if (uuid) {
      await updateFormularioSimByUUID(uuid, payload);
    }

    // Se estiver na etapa de certificação (Step5), redireciona ao salvar
    if (
      (step2Data.hasFamilyFarmersMarket === true && activeStep === 4) ||
      (step2Data.hasFamilyFarmersMarket === false && activeStep === 4)
    ) {
      navigate('/pacote-de-formularios');
      return;
    }

    // Se não possui feira, pula direto para o Step5 (activeStep 4)
    if (step2Data.hasFamilyFarmersMarket === false && activeStep === 1) {
      if (activeStep < 4) {
        setActiveStep(4);
      }
    } else {
      // Fluxo normal
      if (activeStep < 4) {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  return (
    <ContainerPainel pagina="Editar Comercialização" showBackButton={true}>
      <Stack marginX={'4rem'} paddingTop={'2rem'}>
        <FormSimHorizontalStepper
          activeStep={activeStep + 1}
          hasFamilyFarmersMarket={step2Data.hasFamilyFarmersMarket}
          edit={true}
        />

        <Box mt={2}>
          {activeStep === 0 && (
            <Step1
              onNext={handleSubmit}
              onChange={setStep1Data}
              data={step1Data}
            />
          )}

          {activeStep === 1 && (
            <Step2
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep2Data}
              data={step2Data}
            />
          )}

          {/* Se possui feira, Step3, Step4, Step5 normalmente */}
          {step2Data.hasFamilyFarmersMarket === true && activeStep === 2 && (
            <Step3
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep3Data}
              data={step3Data}
              referenceYear={step3Data.referenceYear}
            />
          )}
          {step2Data.hasFamilyFarmersMarket === true && activeStep === 3 && (
            <Step4
              onNext={handleSubmit}
              onBack={handleBack}
              onChange={setStep4Data}
              data={step4Data}
              referenceYear={step4Data.referenceYear}
            />
          )}
          {((step2Data.hasFamilyFarmersMarket === true && activeStep === 4) ||
            (step2Data.hasFamilyFarmersMarket === false && activeStep === 4)) && (
              <Step5
                onNext={handleSubmit}
                onBack={handleBack}
                onChange={setStep5Data}
                data={step5Data}
              />
            )}
        </Box>
      </Stack>
    </ContainerPainel>
  );
};

async function fetchSimFormData(uuid: string) {
  const data = await getFormularioSimByUUID(uuid);

  return {
    step1: {
      referenceYear: data?.reference_year ?? new Date().getFullYear(),
      hasSim: data?.has_sim ?? null,
      simActive: data?.sim_active ?? null,
      simEstablishments: data?.sim_establishments ?? 0,
      hasVet: data?.has_vet ?? null,
      joinedSusaf: data?.joined_susaf ?? null,
      wantsSusaf: data?.wants_susaf ?? null,
    },
    step2: {
      referenceYear: data?.reference_year ?? new Date().getFullYear(),
      hasFamilyFarmersMarket: data?.has_family_farmers_market ?? null,
      numberOfFamilyFarmers: data?.number_of_family_farmers ?? 0,
      annualMarketValue: parseFloat(data?.annual_market_value ?? 0),
      totalFamilyFarmingValue: parseFloat(data?.total_family_farming_value ?? 0),
      numberOfPermanentMarkets: data?.number_of_permanent_markets ?? 0,
      numberOfNonPermanentMarkets: data?.number_of_non_permanent_markets ?? 0,
    },
    step3: {
      referenceYear: data?.reference_year ?? new Date().getFullYear(),
      permanentMarkets: data?.permanent_markets ?? [],
    },
    step4: {
      referenceYear: data?.reference_year ?? new Date().getFullYear(),
      nonPermanentMarkets: data?.non_permanent_markets ?? [],
    },
    step5: {
      hasCertification: data?.has_certification ?? null,
      certificationSoldInFairs: data?.certification_sold_in_fairs ?? null,
      hasMunicipalSeal: data?.has_municipal_seal ?? null,
      municipalSealName: data?.municipal_seal_name ?? '',
      wantsMunicipalSeal: data?.wants_municipal_seal ?? null,
      knowsHowToCreateSeal: data?.knows_how_to_create_seal ?? null,
      referenceYear: Number(data?.reference_year) || new Date().getFullYear(),
    },
  };
}

export default FormularioSIMEditar;